/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Pencil, RefreshCw, Server } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type { OnExpiry, PriceBasisRestore, PriceBasisTransfer, TldSettingsContent, TldSettingsRow, TldSettingsVersion } from '../../api/areas/services';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { hubUrl } from '../../nav/hubs';
import { Link } from 'react-router';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { CheckField, daysList, instant, NumberField, useEnum } from './shared';

const DEFAULTS: TldSettingsContent = {
  transfer_in_allowed: true,
  transfer_adds_years: 1,
  transfer_years_max: 1,
  transfer_price_basis: 'RENEWAL',
  restore_allowed: true,
  restore_adds_years: 1,
  restore_price_basis: 'OWN_PRICE_PLUS_RENEWAL',
  grace_days: 30,
  redemption_days: 30,
  pending_delete_days: 5,
  reminders_before_days: [30, 7],
  reminders_after_days: [5],
  auto_renew_lead_days: 1,
  on_expiry: 'CLIENT_HOLD',
  registry_auto_renews: false,
  auto_renew_grace_days: 0,
  delete_before_argp_end_days: 0,
};

function Select<T extends string>({ label, value, options, group, onChange }: { label: string; value: T; options: readonly T[]; group: string; onChange: (v: T) => void }) {
  const e = useEnum();
  return (
    <Field label={label}>
      <select className={selectClass} value={value} onChange={(ev) => onChange(ev.target.value as T)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {e(group, o)}
          </option>
        ))}
      </select>
    </Field>
  );
}

function SettingsForm({ close, changed, row }: DialogBodyProps & { row: TldSettingsRow }) {
  const { t } = useTranslation('services');
  const [c, setC] = useState<TldSettingsContent>(row.settings ? { ...row.settings } : DEFAULTS);
  const [before, setBefore] = useState(daysList.show(c.reminders_before_days));
  const [after, setAfter] = useState(daysList.show(c.reminders_after_days));
  const [from, setFrom] = useState('');
  const [reason, setReason] = useState('');
  const set = (patch: Partial<TldSettingsContent>) => setC((x) => ({ ...x, ...patch }));
  const w = useWrite((_: null, opts) => {
    const body: TldSettingsContent = {
      transfer_in_allowed: c.transfer_in_allowed,
      transfer_adds_years: c.transfer_adds_years,
      transfer_years_max: c.transfer_years_max,
      transfer_price_basis: c.transfer_price_basis,
      restore_allowed: c.restore_allowed,
      restore_adds_years: c.restore_adds_years,
      restore_price_basis: c.restore_price_basis,
      grace_days: c.grace_days,
      redemption_days: c.redemption_days,
      pending_delete_days: c.pending_delete_days,
      reminders_before_days: daysList.parse(before),
      reminders_after_days: daysList.parse(after),
      auto_renew_lead_days: c.auto_renew_lead_days,
      on_expiry: c.on_expiry,
      registry_auto_renews: c.registry_auto_renews,
      auto_renew_grace_days: c.registry_auto_renews ? c.auto_renew_grace_days : 0,
      delete_before_argp_end_days: c.registry_auto_renews ? c.delete_before_argp_end_days : 0,
    };
    return api.publishTldSettings(row.tld_id, { ...body, effective_from: instant(from), reason: reason.trim() }, opts);
  });
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('common.done')}</Callout>
      ) : (
        <>
          {row.icann_governed ? <Callout tone="info">{t('tld.errp')}</Callout> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <CheckField label={t('tld.form.transferAllowed')} checked={c.transfer_in_allowed} onChange={(v) => set({ transfer_in_allowed: v })} />
            <Select<PriceBasisTransfer>
              label={t('tld.form.transferBasis')}
              value={c.transfer_price_basis}
              options={['FREE', 'RENEWAL', 'OWN_PRICE']}
              group="transferBasis"
              onChange={(v) => set({ transfer_price_basis: v })}
            />
            <NumberField label={t('tld.form.transferYears')} value={c.transfer_adds_years} max={10} onChange={(v) => set({ transfer_adds_years: v })} />
            <NumberField label={t('tld.form.transferMax')} value={c.transfer_years_max} max={10} onChange={(v) => set({ transfer_years_max: v })} />
            <CheckField label={t('tld.form.restoreAllowed')} checked={c.restore_allowed} onChange={(v) => set({ restore_allowed: v })} />
            <Select<PriceBasisRestore>
              label={t('tld.form.restoreBasis')}
              value={c.restore_price_basis}
              options={['FREE', 'RENEWAL', 'OWN_PRICE', 'OWN_PRICE_PLUS_RENEWAL']}
              group="restoreBasis"
              onChange={(v) => set({ restore_price_basis: v })}
            />
            <NumberField label={t('tld.form.restoreYears')} value={c.restore_adds_years} max={10} onChange={(v) => set({ restore_adds_years: v })} />
            <Select<OnExpiry>
              label={t('tld.form.onExpiry')}
              value={c.on_expiry}
              options={['PARKING_NS', 'REGISTRY_HANDLES', 'CLIENT_HOLD', 'NONE']}
              group="onExpiry"
              onChange={(v) => set({ on_expiry: v })}
            />
            <NumberField label={t('tld.form.grace')} value={c.grace_days} onChange={(v) => set({ grace_days: v })} />
            <NumberField label={t('tld.form.redemption')} value={c.redemption_days} onChange={(v) => set({ redemption_days: v })} />
            <NumberField label={t('tld.form.pendingDelete')} value={c.pending_delete_days} onChange={(v) => set({ pending_delete_days: v })} />
            <NumberField label={t('tld.form.lead')} value={c.auto_renew_lead_days} max={60} onChange={(v) => set({ auto_renew_lead_days: v })} />
            <Field label={t('tld.form.before')}>
              <Input value={before} onChange={(ev) => setBefore(ev.target.value)} />
            </Field>
            <Field label={t('tld.form.after')}>
              <Input value={after} onChange={(ev) => setAfter(ev.target.value)} />
            </Field>
            <CheckField label={t('tld.form.autoRenews')} checked={c.registry_auto_renews} onChange={(v) => set({ registry_auto_renews: v })} />
            <div />
            {c.registry_auto_renews ? (
              <>
                <NumberField label={t('tld.form.argp')} value={c.auto_renew_grace_days} max={60} onChange={(v) => set({ auto_renew_grace_days: v })} />
                <NumberField label={t('tld.form.margin')} value={c.delete_before_argp_end_days} max={60} onChange={(v) => set({ delete_before_argp_end_days: v })} />
              </>
            ) : null}
            <Field label={t('common.effectiveFrom')} hint={t('common.effectiveFromHint')}>
              <Input type="datetime-local" value={from} onChange={(ev) => setFrom(ev.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">{t('tld.stageSkipped')}</p>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('common.save')} onClose={close} />
    </form>
  );
}

/** A versioned set of name servers (the parking pair, or the default ones of new registrations). */
function ParkingForm({ close, changed, current, save }: DialogBodyProps & { current: string[]; save: typeof api.setParkingNameservers }) {
  const { t } = useTranslation('services');
  const [hosts, setHosts] = useState(current.join('\n'));
  const [reason, setReason] = useState('');
  // Counted as the server counts them: lower case, no final dot, each host once.
  const list = [...new Set(hosts.split(/\s+/).map((h) => h.toLowerCase().replace(/\.$/, '')).filter(Boolean))];
  const tooFew = list.length < 2 || list.length > 13;
  const w = useWrite((_: null, opts) => save({ nameservers: list, reason: reason.trim() }, opts));
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('common.done')}</Callout>
      ) : (
        <>
          <Field label={t('tld.parkingField')}>
            <Textarea rows={3} value={hosts} onChange={(ev) => setHosts(ev.target.value)} className="font-mono" />
          </Field>
          {tooFew ? <p className="text-xs text-critical">{t('tld.nsTooFew')}</p> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || tooFew} submit={done ? undefined : t('common.save')} onClose={close} />
    </form>
  );
}

/**
 * Per-TLD transfer, restore and expiry terms, the parking name servers and the default name
 * servers of new registrations. `part` shows only the terms or only the name servers (Settings ›
 * Domains & TLDs gives each a tab).
 */
export function TldSettingsPage({ part = 'all' }: { part?: 'all' | 'terms' | 'parking' }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  useDocumentTitle(t('tld.title'));
  const rows = useApi((signal) => api.listTldSettings({ signal }), []);
  const parking = useApi((signal) => api.getParkingNameservers({ signal }), []);
  const defaults = useApi((signal) => api.getDefaultNameservers({ signal }), []);
  const reload = () => {
    rows.reload();
    parking.reload();
    defaults.reload();
  };
  const d = (n: number) => t('common.days', { count: n });

  const cols: Column<TldSettingsRow>[] = [
    {
      key: 'zone',
      header: t('tld.col.zone'),
      cell: (r) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-semibold">.{r.zone}</span>
          <Badge tone="neutral" icon={false}>
            {e('tldClass', r.tld_class)}
          </Badge>
        </span>
      ),
    },
    {
      key: 'transfer',
      header: t('tld.col.transfer'),
      cell: (r) =>
        !r.settings ? (
          <span className="text-muted-foreground">{t('tld.policyFiles')}</span>
        ) : r.settings.transfer_in_allowed ? (
          `${e('transferBasis', r.settings.transfer_price_basis)} · ${t('tld.addsYears', { count: r.settings.transfer_adds_years })}`
        ) : (
          t('tld.notAllowed')
        ),
    },
    {
      key: 'restore',
      header: t('tld.col.restore'),
      cell: (r) => (!r.settings ? t('common.none') : r.settings.restore_allowed ? e('restoreBasis', r.settings.restore_price_basis) : <Badge tone="warn">{t('tld.notAllowed')}</Badge>),
    },
    {
      key: 'stages',
      header: t('tld.col.stages'),
      className: 'whitespace-nowrap',
      cell: (r) => (r.settings ? `${d(r.settings.grace_days)} / ${d(r.settings.redemption_days)} / ${d(r.settings.pending_delete_days)}` : t('common.none')),
    },
    { key: 'expiry', header: t('tld.col.onExpiry'), cell: (r) => (r.settings ? e('onExpiry', r.settings.on_expiry) : t('common.none')) },
    {
      key: 'auto',
      header: t('tld.col.autoRenew'),
      cell: (r) =>
        r.settings?.registry_auto_renews ? (
          <span className="text-xs">{t('tld.argp', { argp: r.settings.auto_renew_grace_days, margin: r.settings.delete_before_argp_end_days })}</span>
        ) : (
          t('common.no')
        ),
    },
    { key: 'version', header: t('tld.col.version'), cell: (r) => (r.settings ? `v${r.settings.version} · ${formatDateTime(r.settings.effective_from)}` : t('common.none')) },
    {
      key: 'edit',
      header: '',
      cell: (r) => (
        <ActionDialog label={t('tld.edit')} icon={Pencil} variant="ghost" title={t('tld.editTitle', { zone: r.zone })} wide onChanged={reload}>
          {(p) => <SettingsForm {...p} row={r} />}
        </ActionDialog>
      ),
    },
  ];

  const current = parking.status === 'success' ? (parking.data.current?.nameservers ?? []) : [];
  return (
    <>
      <PageHeader
        title={part === 'parking' ? t('tld.parking') : t('tld.title')}
        crumbs={[{ label: t('crumbs.settings') }]}
        subtitle={t('tld.subtitle')}
        actions={
          <Button type="button" variant="outline" size="sm" onClick={reload}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      <div className="flex flex-col gap-6">
        {part !== 'terms' ? (
          <Section
            title={t('tld.parking')}
            description={t('tld.parkingHint')}
            actions={
              <ActionDialog label={t('tld.editParking')} icon={Server} title={t('tld.editParking')} onChanged={reload}>
                {(p) => <ParkingForm {...p} current={current} save={api.setParkingNameservers} />}
              </ActionDialog>
            }
          >
            {parking.status === 'success' && parking.data.current ? (
              <KeyValue
                rows={[
                  [
                    t('tld.parkingField'),
                    <span key="ns" className="font-mono">
                      {parking.data.current.nameservers.join(', ')}
                    </span>,
                  ],
                  [t('tld.col.version'), `v${parking.data.current.version} · ${formatDateTime(parking.data.current.created_at)}`],
                  [t('common.reason'), parking.data.current.reason],
                ]}
              />
            ) : (
              <Loading rows={1} />
            )}
          </Section>
        ) : null}
        {part !== 'terms' ? (
          <Section
            title={t('tld.defaultNs')}
            description={t('tld.defaultNsHint')}
            actions={
              <ActionDialog label={t('tld.editDefaultNs')} icon={Server} title={t('tld.editDefaultNs')} onChanged={reload}>
                {(p) => <ParkingForm {...p} current={defaults.status === 'success' ? (defaults.data.current?.nameservers ?? []) : []} save={api.setDefaultNameservers} />}
              </ActionDialog>
            }
          >
            {defaults.status === 'loading' ? <Loading rows={1} /> : null}
            {defaults.status === 'error' ? <DataError error={defaults.error} title={t('common.loadFailed')} onRetry={defaults.reload} /> : null}
            {defaults.status === 'success' && defaults.data.current ? (
              <KeyValue
                rows={[
                  [
                    t('tld.parkingField'),
                    <span key="ns" className="font-mono">
                      {defaults.data.current.nameservers.join(', ')}
                    </span>,
                  ],
                  [t('tld.col.version'), `v${defaults.data.current.version} · ${formatDateTime(defaults.data.current.created_at)}`],
                  [t('common.reason'), defaults.data.current.reason],
                ]}
              />
            ) : null}
          </Section>
        ) : null}
        {part !== 'parking' ? (
          <Section flush>
            {rows.status === 'loading' ? <Loading rows={4} /> : null}
            {rows.status === 'error' ? <DataError error={rows.error} title={t('common.loadFailed')} onRetry={rows.reload} /> : null}
            {rows.status === 'success' ? <DataTable columns={cols} rows={rows.data.items} rowKey={(r) => r.tld_id} /> : null}
          </Section>
        ) : null}
      </div>
    </>
  );
}

/** The terms of one version, grouped (transfer-in, restore, expiry stages, reminders, auto-renew). */
function TermsView({ v }: { v: TldSettingsContent }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const d = (n: number) => (n === 0 ? `0 (${t('tld.skipped')})` : t('common.days', { count: n }));
  const yes = (b: boolean) => (b ? t('common.yes') : t('common.no'));
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section title={t('tld.sections.transfer')}>
        <KeyValue
          rows={[
            [t('tld.form.transferAllowed'), yes(v.transfer_in_allowed)],
            [t('tld.form.transferBasis'), e('transferBasis', v.transfer_price_basis)],
            [t('tld.form.transferYears'), t('common.years', { count: v.transfer_adds_years })],
            [t('tld.form.transferMax'), t('common.years', { count: v.transfer_years_max })],
          ]}
        />
      </Section>
      <Section title={t('tld.sections.restore')}>
        <KeyValue
          rows={[
            [t('tld.form.restoreAllowed'), v.restore_allowed ? t('common.yes') : <Badge key="r" tone="warn">{t('tld.notAllowed')}</Badge>],
            [t('tld.form.restoreBasis'), v.restore_allowed ? e('restoreBasis', v.restore_price_basis) : t('common.none')],
            [t('tld.form.restoreYears'), v.restore_allowed ? t('common.years', { count: v.restore_adds_years }) : t('common.none')],
          ]}
        />
      </Section>
      <Section title={t('tld.sections.expiry')}>
        <KeyValue
          rows={[
            [t('tld.form.grace'), d(v.grace_days)],
            [t('tld.form.redemption'), d(v.redemption_days)],
            [t('tld.form.pendingDelete'), d(v.pending_delete_days)],
            [t('tld.form.onExpiry'), e('onExpiry', v.on_expiry)],
          ]}
        />
      </Section>
      <Section title={t('tld.sections.reminders')}>
        <KeyValue
          rows={[
            [t('tld.form.before'), daysList.show(v.reminders_before_days) || t('common.none')],
            [t('tld.form.after'), daysList.show(v.reminders_after_days) || t('common.none')],
            [t('tld.form.lead'), t('common.days', { count: v.auto_renew_lead_days })],
            [t('tld.form.autoRenews'), v.registry_auto_renews ? t('tld.argp', { argp: v.auto_renew_grace_days, margin: v.delete_before_argp_end_days }) : t('common.no')],
          ]}
        />
      </Section>
    </div>
  );
}

/**
 * One TLD's lifecycle, transfer-in and restore terms (the TLD page's tab): the version in force,
 * edit (a new version with an effective date and a reason), and every version. Only the parking
 * name-server pair is shared by every TLD; it is shown with a link to where it is changed.
 */
export function TldTermsPanel({ tldId, zone }: { tldId: string; zone: string }) {
  const { t } = useTranslation('services');
  const detail = useApi((signal) => api.getTldSettings(tldId, { signal }), [tldId]);
  const parking = useApi((signal) => api.getParkingNameservers({ signal }), []);
  const reload = () => {
    detail.reload();
    parking.reload();
  };
  if (detail.status === 'loading') return <Loading rows={4} />;
  if (detail.status === 'error') return <DataError error={detail.error} title={t('common.loadFailed')} onRetry={detail.reload} />;
  const x = detail.data;
  const row: TldSettingsRow = { tld_id: x.tld_id, zone: x.zone, tld_class: x.tld_class, icann_governed: x.icann_governed, settings: x.current, scheduled: 0 };
  const now = new Date().toISOString();
  const scheduled = x.versions.filter((v) => v.effective_from > now);
  const versionCols: Column<TldSettingsVersion>[] = [
    { key: 'v', header: t('tld.col.version'), cell: (v) => <span className="font-medium">v{v.version}</span> },
    { key: 'from', header: t('common.effectiveFrom'), className: 'whitespace-nowrap', cell: (v) => formatDateTime(v.effective_from) },
    { key: 'stages', header: t('tld.col.stages'), className: 'whitespace-nowrap', cell: (v) => `${t('common.days', { count: v.grace_days })} / ${t('common.days', { count: v.redemption_days })} / ${t('common.days', { count: v.pending_delete_days })}` },
    { key: 'state', header: t('tld.col.state'), cell: (v) => (v.id === x.current?.id ? <Badge tone="ok">{t('tld.inForce')}</Badge> : v.effective_from > now ? <Badge tone="info">{t('tld.scheduled')}</Badge> : <Badge tone="neutral">{t('tld.superseded')}</Badge>) },
    { key: 'reason', header: t('common.reason'), cell: (v) => <span className="text-xs">{v.reason}</span> },
  ];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('tld.termsTitle', { zone: `.${zone}` })}
        subtitle={t('tld.termsSubtitle')}
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={reload}>
              <RefreshCw /> {t('common.refresh')}
            </Button>
            <ActionDialog label={t('tld.edit')} icon={Pencil} title={t('tld.editTitle', { zone })} wide onChanged={reload}>
              {(p) => <SettingsForm {...p} row={row} />}
            </ActionDialog>
          </>
        }
      />
      {x.current ? (
        <>
          <p className="text-sm text-muted-foreground">{t('tld.inForceSince', { version: x.current.version, from: formatDateTime(x.current.effective_from), reason: x.current.reason })}</p>
          <TermsView v={x.current} />
        </>
      ) : (
        <Callout tone="warn" title={t('tld.noSettingsTitle')}>
          <p>{t('tld.noSettings')}</p>
        </Callout>
      )}
      {scheduled.length ? <Callout tone="info">{t('tld.scheduledNote', { count: scheduled.length })}</Callout> : null}
      <Section title={t('tld.versions')} flush>
        <DataTable columns={versionCols} rows={x.versions} rowKey={(v) => v.id} empty={<p className="p-4 text-sm text-muted-foreground">{t('tld.noVersions')}</p>} />
      </Section>
      <Section title={t('tld.parking')} description={t('tld.parkingShared')}>
        {parking.status === 'success' && parking.data.current ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-mono">{parking.data.current.nameservers.join(', ')}</span>
            <Link to={hubUrl('settings', 'domains', 'lifecycle')} className="text-primary underline-offset-4 hover:underline">
              {t('tld.parkingWhere')}
            </Link>
          </div>
        ) : (
          <Loading rows={1} />
        )}
      </Section>
    </div>
  );
}
